import { Language } from '../types';

/**
 * Standard automatic dictionary for common restaurant / shop menus and categories.
 * Translates standard terms into all 10 supported languages instantly without requiring manual AI translation.
 */
export const STANDARD_MENU_TRANSLATIONS: Record<string, Record<Language, string>> = {
  // Menus & Concepts
  'dolce': {
    it: 'Dolce',
    en: 'Desserts',
    fr: 'Desserts',
    es: 'Postres',
    de: 'Desserts',
    pt: 'Sobremesas',
    ru: 'Десерты',
    nl: 'Nagerechten',
    cz: 'Dezerty',
    hr: 'Deserti'
  },
  'dolci': {
    it: 'Dolci',
    en: 'Desserts',
    fr: 'Desserts',
    es: 'Postres',
    de: 'Desserts',
    pt: 'Sobremesas',
    ru: 'Десерты',
    nl: 'Nagerechten',
    cz: 'Dezerty',
    hr: 'Deserti'
  },
  'salato': {
    it: 'Salato',
    en: 'Savory',
    fr: 'Salé',
    es: 'Salado',
    de: 'Herzhaft',
    pt: 'Salgados',
    ru: 'Закуски',
    nl: 'Hartig',
    cz: 'Slané',
    hr: 'Slano'
  },
  'salati': {
    it: 'Salati',
    en: 'Savory',
    fr: 'Salés',
    es: 'Salados',
    de: 'Herzhafte Speisen',
    pt: 'Salgados',
    ru: 'Закуски',
    nl: 'Hartig',
    cz: 'Slané',
    hr: 'Slano'
  },
  'bevanda': {
    it: 'Bevanda',
    en: 'Drink',
    fr: 'Boisson',
    es: 'Bebida',
    de: 'Getränk',
    pt: 'Bebida',
    ru: 'Напиток',
    nl: 'Drank',
    cz: 'Nápoj',
    hr: 'Piće'
  },
  'bevande': {
    it: 'Bevande',
    en: 'Drinks',
    fr: 'Boissons',
    es: 'Bebidas',
    de: 'Getränke',
    pt: 'Bebidas',
    ru: 'Напитки',
    nl: 'Dranken',
    cz: 'Nápoje',
    hr: 'Pića'
  },
  'bibite': {
    it: 'Bibite',
    en: 'Soft Drinks',
    fr: 'Boissons fraîches',
    es: 'Refrescos',
    de: 'Erfrischungsgetränke',
    pt: 'Refrigerantes',
    ru: 'Безалкогольные напитки',
    nl: 'Frisdranken',
    cz: 'Nealko nápoje',
    hr: 'Bezalkoholna pića'
  },
  'antipasti': {
    it: 'Antipasti',
    en: 'Starters',
    fr: 'Entrées',
    es: 'Entrantes',
    de: 'Vorspeisen',
    pt: 'Entradas',
    ru: 'Закуски',
    nl: 'Voorgerechten',
    cz: 'Předkrmy',
    hr: 'Predjela'
  },
  'antipasto': {
    it: 'Antipasto',
    en: 'Starter',
    fr: 'Entrée',
    es: 'Entrante',
    de: 'Vorspeise',
    pt: 'Entrada',
    ru: 'Закуска',
    nl: 'Voorgerecht',
    cz: 'Předkrm',
    hr: 'Predjelo'
  },
  'primi': {
    it: 'Primi',
    en: 'First Courses',
    fr: 'Plats principaux',
    es: 'Primeros Platos',
    de: 'Erste Gänge',
    pt: 'Primeiros Pratos',
    ru: 'Первые блюда',
    nl: 'Hoofdgerechten',
    cz: 'První chody',
    hr: 'Prva jela'
  },
  'secondi': {
    it: 'Secondi',
    en: 'Main Courses',
    fr: 'Second plats',
    es: 'Segundos Platos',
    de: 'Hauptgerichte',
    pt: 'Pratos Principais',
    ru: 'Вторые блюда',
    nl: 'Hoofdgerechten',
    cz: 'Hlavní jídla',
    hr: 'Glavna jela'
  },
  'contorni': {
    it: 'Contorni',
    en: 'Side Dishes',
    fr: 'Accompagnements',
    es: 'Guarniciones',
    de: 'Beilagen',
    pt: 'Acompanhamentos',
    ru: 'Гарниры',
    nl: 'Bijgerechten',
    cz: 'Přílohy',
    hr: 'Prilozi'
  },
  'pizze': {
    it: 'Pizze',
    en: 'Pizzas',
    fr: 'Pizzas',
    es: 'Pizzas',
    de: 'Pizzen',
    pt: 'Pizzas',
    ru: 'Пиццы',
    nl: 'Pizza\'s',
    cz: 'Pizzy',
    hr: 'Pizze'
  },
  'pizza': {
    it: 'Pizza',
    en: 'Pizza',
    fr: 'Pizza',
    es: 'Pizza',
    de: 'Pizza',
    pt: 'Pizza',
    ru: 'Пицца',
    nl: 'Pizza',
    cz: 'Pizza',
    hr: 'Pizza'
  },
  'panini': {
    it: 'Panini',
    en: 'Sandwiches',
    fr: 'Sandwichs',
    es: 'Bocadillos',
    de: 'Sandwiches',
    pt: 'Sanduíches',
    ru: 'Сэндвичи',
    nl: 'Broodjes',
    cz: 'Sendviče',
    hr: 'Sendviči'
  },
  'panino': {
    it: 'Panino',
    en: 'Sandwich',
    fr: 'Sandwich',
    es: 'Bocadillo',
    de: 'Sandwich',
    pt: 'Sanduíche',
    ru: 'Сэндвич',
    nl: 'Broodje',
    cz: 'Sendvič',
    hr: 'Sendvič'
  },
  'burger': {
    it: 'Burger',
    en: 'Burgers',
    fr: 'Burgers',
    es: 'Hamburguesas',
    de: 'Burger',
    pt: 'Hambúrgueres',
    ru: 'Бургеры',
    nl: 'Burgers',
    cz: 'Burgery',
    hr: 'Burger'
  },
  'hamburgers': {
    it: 'Hamburgers',
    en: 'Burgers',
    fr: 'Burgers',
    es: 'Hamburguesas',
    de: 'Burger',
    pt: 'Hambúrgueres',
    ru: 'Бургеры',
    nl: 'Burgers',
    cz: 'Burgery',
    hr: 'Burger'
  },
  'fritti': {
    it: 'Fritti',
    en: 'Fried Items',
    fr: 'Fritures',
    es: 'Fritos',
    de: 'Frittiertes',
    pt: 'Fritos',
    ru: 'Фритюр',
    nl: 'Gefrituurd',
    cz: 'Smažené',
    hr: 'Prženo'
  },
  'birre': {
    it: 'Birre',
    en: 'Beers',
    fr: 'Bières',
    es: 'Cervezas',
    de: 'Biere',
    pt: 'Cervejas',
    ru: 'Пиво',
    nl: 'Bieren',
    cz: 'Piva',
    hr: 'Piva'
  },
  'birra': {
    it: 'Birra',
    en: 'Beer',
    fr: 'Bière',
    es: 'Cerveza',
    de: 'Bier',
    pt: 'Cerveja',
    ru: 'Пиво',
    nl: 'Bier',
    cz: 'Pivo',
    hr: 'Pivo'
  },
  'vini': {
    it: 'Vini',
    en: 'Wines',
    fr: 'Vins',
    es: 'Vinos',
    de: 'Weine',
    pt: 'Vinhos',
    ru: 'Вина',
    nl: 'Wijnen',
    cz: 'Vína',
    hr: 'Vina'
  },
  'vino': {
    it: 'Vino',
    en: 'Wine',
    fr: 'Vin',
    es: 'Vino',
    de: 'Wein',
    pt: 'Vinho',
    ru: 'Вино',
    nl: 'Wijn',
    cz: 'Víno',
    hr: 'Vino'
  },
  'cocktail': {
    it: 'Cocktail',
    en: 'Cocktails',
    fr: 'Cocktails',
    es: 'Cócteles',
    de: 'Cocktails',
    pt: 'Cocktails',
    ru: 'Коктейли',
    nl: 'Cocktails',
    cz: 'Koktejly',
    hr: 'Kokteli'
  },
  'cocktails': {
    it: 'Cocktails',
    en: 'Cocktails',
    fr: 'Cocktails',
    es: 'Cócteles',
    de: 'Cocktails',
    pt: 'Cocktails',
    ru: 'Коктейли',
    nl: 'Cocktails',
    cz: 'Koktejly',
    hr: 'Kokteli'
  },
  'caffetteria': {
    it: 'Caffetteria',
    en: 'Coffee & Cafeteria',
    fr: 'Cafétéria',
    es: 'Cafetería',
    de: 'Café & Kaffee',
    pt: 'Cafetaria',
    ru: 'Кофейня',
    nl: 'Koffie & Café',
    cz: 'Kavárna',
    hr: 'Kava i napici'
  },
  'caffè': {
    it: 'Caffè',
    en: 'Coffee',
    fr: 'Café',
    es: 'Café',
    de: 'Kaffee',
    pt: 'Café',
    ru: 'Кофе',
    nl: 'Koffie',
    cz: 'Káva',
    hr: 'Kava'
  },
  'gelati': {
    it: 'Gelati',
    en: 'Ice Cream',
    fr: 'Glaces',
    es: 'Helados',
    de: 'Eis',
    pt: 'Gelados',
    ru: 'Мороженое',
    nl: 'IJs',
    cz: 'Zmrzlina',
    hr: 'Sladoled'
  },
  'gelato': {
    it: 'Gelato',
    en: 'Ice Cream',
    fr: 'Glace',
    es: 'Helado',
    de: 'Eis',
    pt: 'Gelado',
    ru: 'Мороженое',
    nl: 'IJs',
    cz: 'Zmrzlina',
    hr: 'Sladoled'
  },
  'piadine': {
    it: 'Piadine',
    en: 'Flatbread Wraps',
    fr: 'Piadines',
    es: 'Piadinas',
    de: 'Piadine',
    pt: 'Piadinas',
    ru: 'Пьядина',
    nl: 'Piadinas',
    cz: 'Piadiny',
    hr: 'Piadine'
  },
  'snack': {
    it: 'Snack',
    en: 'Snacks',
    fr: 'Snacks',
    es: 'Snacks',
    de: 'Snacks',
    pt: 'Snacks',
    ru: 'Снэки',
    nl: 'Snacks',
    cz: 'Snacky',
    hr: 'Grickalice'
  },
  'menu': {
    it: 'Menu',
    en: 'Menu',
    fr: 'Menu',
    es: 'Menú',
    de: 'Menü',
    pt: 'Menu',
    ru: 'Меню',
    nl: 'Menu',
    cz: 'Menu',
    hr: 'Jelovnik'
  }
};

/**
 * Helper to get a standardized translation if available
 */
export const getStandardTranslation = (text: string | undefined | null, targetLang: Language): string | null => {
  if (!text || typeof text !== 'string') return null;
  const cleanKey = text.trim().toLowerCase();
  
  if (STANDARD_MENU_TRANSLATIONS[cleanKey] && STANDARD_MENU_TRANSLATIONS[cleanKey][targetLang]) {
    return STANDARD_MENU_TRANSLATIONS[cleanKey][targetLang];
  }
  
  // Try matching without punctuation
  const strippedKey = cleanKey.replace(/[^\p{L}\s]/gu, '').trim();
  if (STANDARD_MENU_TRANSLATIONS[strippedKey] && STANDARD_MENU_TRANSLATIONS[strippedKey][targetLang]) {
    return STANDARD_MENU_TRANSLATIONS[strippedKey][targetLang];
  }
  
  return null;
};
